import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Globe2, ExternalLink, Lock, Plus, Building2, Scale, Landmark, ShieldQuestion, X,
} from "lucide-react";
import { toast } from "sonner";

/**
 * The locality library — reference material pooled across estates in one council area.
 *
 * This is the only screen in the product where data deliberately crosses the estate
 * boundary, so it carries its own standing explanation of what can and cannot be here. The
 * rule is structural, not a setting: only `reference`-class documents are eligible, so a
 * deed or a service charge statement can never appear on this page however the estate is
 * configured.
 *
 * Provenance is always shown. A commons where you can't see who contributed what — and
 * when it was last checked — is just an unsourced pile, and blocking rule 1.4 means
 * anything dated has to show its date.
 */

const AUTHORITIES = [
    { key: "legislation", label: "Legislation", icon: Scale },
    { key: "government", label: "Government", icon: Landmark },
    { key: "council", label: "Council", icon: Building2 },
    { key: "parish", label: "Parish / town", icon: Building2 },
    { key: "ombudsman", label: "Ombudsman / redress", icon: ShieldQuestion },
    { key: "professional_body", label: "Professional body", icon: ShieldQuestion },
    { key: "other", label: "Other", icon: Globe2 },
];

const authorityMeta = (key) =>
    AUTHORITIES.find((a) => a.key === key) || AUTHORITIES[AUTHORITIES.length - 1];

/** Cross-tenant links: only http/https become hrefs. `javascript:` executes on click. */
function isHttpOrHttpsUrl(url) {
    if (!url || typeof url !== "string") return false;
    const trimmed = url.trim();
    if (trimmed.startsWith("//") || trimmed.startsWith("\\\\")) return false;
    try {
        const parsed = new URL(trimmed);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
        return false;
    }
}

export default function LocalityLibrary() {
    const { user } = useAuth();
    const isAdmin = user?.role === "estate_admin";

    const [resources, setResources] = useState([]);
    const [workspace, setWorkspace] = useState(null);
    const [filter, setFilter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [civic, setCivic] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({
        title: "",
        description: "",
        authority: "council",
        source_url: "",
        published_on: "",
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [r, w, c] = await Promise.all([
                api.get("/locality/resources", { params: filter ? { authority: filter } : {} }),
                api.get("/onboarding/workspace"),
                api.get("/locality/civic"),
            ]);
            setResources(r.data);
            setWorkspace(w.data);
            setCivic(c.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load the local library");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    const add = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/locality/resources", {
                ...form,
                description: form.description || null,
                published_on: form.published_on || null,
            });
            toast.success("Shared with your local area");
            setForm({ title: "", description: "", authority: "council", source_url: "", published_on: "" });
            setShowAdd(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not share that");
        } finally {
            setBusy(false);
        }
    };

    const withdraw = async (id) => {
        try {
            await api.post(`/locality/resources/${id}/withdraw`);
            toast.success("Withdrawn from the local library");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not withdraw that");
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-6 py-10" data-testid="locality-library-page">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                <Globe2 size={14} /> Shared across your local area
            </div>
            <div className="flex items-start justify-between gap-4 mt-2 flex-wrap">
                <div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900">Local reference library</h1>
                    <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                        Regulations, council guidance and standard forms that nearby estates have found and
                        shared. Rules that apply to one estate usually apply to its neighbours too — so
                        nobody needs to find them twice.
                    </p>
                </div>
                {isAdmin && (
                    <Button
                        onClick={() => setShowAdd((s) => !s)}
                        className="bg-slate-900 hover:bg-slate-800 text-white h-9 text-xs shrink-0"
                        data-testid="locality-add-toggle"
                    >
                        {showAdd ? <X size={14} className="mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
                        {showAdd ? "Cancel" : "Share something"}
                    </Button>
                )}
            </div>

            {/* The standing privacy explanation. This screen is where people will worry. */}
            <div className="mt-6 rounded-md bg-slate-50 border border-slate-200 p-4 flex gap-3">
                <Lock size={16} className="text-slate-500 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-600 leading-relaxed">
                    <strong className="text-slate-900">Only reference material can be here.</strong> Deeds,
                    service charge statements, correspondence and anything about a specific household can
                    never be shared to this library — that's built into how documents are classified, not a
                    setting anyone can change. What you see here is public-interest material: regulations,
                    official guidance, standard forms.
                </p>
            </div>

            {civic?.joined && civic.modules?.length > 0 && (
                <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5" data-testid="civic-helpers">
                    <h2 className="font-heading font-semibold text-base text-slate-900">
                        Official pages for {civic.locality_name || "this area"}
                    </h2>
                    <p className="text-xs text-slate-600 mt-1 max-w-2xl">{civic.disclaimer}</p>
                    <ul className="mt-3 space-y-2">
                        {civic.modules.map((m) => (
                            <li key={m.key} className="text-sm">
                                {isHttpOrHttpsUrl(m.url) ? (
                                    <a
                                        href={m.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-slate-900 underline underline-offset-2"
                                    >
                                        {m.title}
                                    </a>
                                ) : (
                                    <span>{m.title}</span>
                                )}
                                <span className="text-xs text-slate-500 ml-2">
                                    {m.source}
                                    {m.checked_on ? ` · checked ${m.checked_on}` : ""}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {!workspace?.claim_status || workspace.claim_status !== "claimed" ? (
                <div className="mt-8 rounded-lg border border-slate-200 bg-white p-8 text-center">
                    <Globe2 size={28} className="mx-auto text-slate-300" />
                    <h2 className="font-heading font-semibold text-lg mt-3">Not part of a local area yet</h2>
                    <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
                        Once a director takes on this workspace for your management company, you can join
                        your council area and see what nearby estates have shared.
                    </p>
                </div>
            ) : (
                <>
                    {/* Filters */}
                    <div className="mt-8 flex flex-wrap gap-1.5">
                        <button
                            onClick={() => setFilter(null)}
                            className={`text-xs px-2.5 py-1 rounded-full border ${
                                !filter ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"
                            }`}
                        >
                            All
                        </button>
                        {AUTHORITIES.map((a) => (
                            <button
                                key={a.key}
                                onClick={() => setFilter(a.key)}
                                className={`text-xs px-2.5 py-1 rounded-full border ${
                                    filter === a.key
                                        ? "bg-slate-900 text-white border-slate-900"
                                        : "bg-white border-slate-200 text-slate-600"
                                }`}
                            >
                                {a.label}
                            </button>
                        ))}
                    </div>

                    {/* Add form */}
                    {showAdd && (
                        <form onSubmit={add} className="mt-5 rounded-lg border border-slate-300 bg-white p-5 space-y-3" data-testid="locality-add-form">
                            <div>
                                <Label htmlFor="lt">What is it?</Label>
                                <Input
                                    id="lt"
                                    required
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    placeholder="Borough parking enforcement policy 2026"
                                    data-testid="locality-title-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="lu">Link to it</Label>
                                <Input
                                    id="lu"
                                    required
                                    type="url"
                                    value={form.source_url}
                                    onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                                    placeholder="https://www.yourcouncil.gov.uk/…"
                                    data-testid="locality-url-input"
                                />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="la">Who published it?</Label>
                                    <select
                                        id="la"
                                        value={form.authority}
                                        onChange={(e) => setForm({ ...form, authority: e.target.value })}
                                        className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                                    >
                                        {AUTHORITIES.map((a) => (
                                            <option key={a.key} value={a.key}>
                                                {a.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <Label htmlFor="lp">Published on (optional)</Label>
                                    <Input
                                        id="lp"
                                        type="date"
                                        value={form.published_on}
                                        onChange={(e) => setForm({ ...form, published_on: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="ld">Why it's useful (optional)</Label>
                                <Textarea
                                    id="ld"
                                    rows={2}
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    placeholder="Sets out how the council enforces on private estate roads."
                                />
                            </div>
                            <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white h-9 text-xs">
                                Share with my local area
                            </Button>
                        </form>
                    )}

                    {/* The library */}
                    <div className="mt-6 space-y-3">
                        {loading && <p className="text-sm text-slate-500">Loading…</p>}
                        {!loading && resources.length === 0 && (
                            <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
                                <p className="text-sm text-slate-600">
                                    Nothing shared here yet.
                                    {isAdmin && " Be the first — anything you add helps every estate nearby."}
                                </p>
                            </div>
                        )}
                        {resources.map((r) => {
                            const meta = authorityMeta(r.authority);
                            const Icon = meta.icon;
                            return (
                                <div
                                    key={r.id}
                                    className="rounded-lg border border-slate-200 bg-white p-4"
                                    data-testid={`locality-resource-${r.id}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <Icon size={18} className="text-slate-400 shrink-0 mt-0.5" />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start justify-between gap-3">
                                                <h3 className="font-medium text-slate-900 leading-snug">{r.title}</h3>
                                                <span className="text-[11px] uppercase tracking-wide text-slate-400 shrink-0">
                                                    {meta.label}
                                                </span>
                                            </div>
                                            {r.description && (
                                                <p className="text-sm text-slate-600 mt-1">{r.description}</p>
                                            )}
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5 text-xs text-slate-500">
                                                {r.source_url && (
                                                    isHttpOrHttpsUrl(r.source_url) ? (
                                                        <a
                                                            href={r.source_url}
                                                            target="_blank"
                                                            rel="noreferrer noopener"
                                                            className="inline-flex items-center gap-1 text-slate-700 underline"
                                                        >
                                                            Open source <ExternalLink size={11} />
                                                        </a>
                                                    ) : (
                                                        <span className="break-all">{r.source_url}</span>
                                                    )
                                                )}
                                                <span>
                                                    Shared by {r.is_own_estate ? "your estate" : r.contributed_by_estate_name}
                                                </span>
                                                {r.published_on && <span>Published {r.published_on}</span>}
                                                {r.checked_on && <span>Checked {r.checked_on}</span>}
                                                {r.is_own_estate && isAdmin && (
                                                    <button
                                                        onClick={() => withdraw(r.id)}
                                                        className="text-slate-500 hover:text-rose-600 underline"
                                                    >
                                                        Withdraw
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <p className="mt-8 text-xs text-slate-500 leading-relaxed max-w-2xl">
                        Links are shared by other residents, not checked or endorsed by us. Always open the
                        source and check the date before relying on anything here. This is reference
                        material, not legal advice.
                    </p>
                </>
            )}
        </div>
    );
}
